import clsx from 'clsx';
import { useDrag, useDrop } from 'react-dnd';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { UploadIcon } from '../icons/upload';
import { useRef, useState } from 'react';
import { TrashIcon } from '../icons/trash';
import { AddPinIcon } from '../icons/add-pin';
import { useMachineStatus } from '../hooks/use-machine-status';
import { ResLoading } from '../shared/res-loading';

export const CadPreview = (ps: {
  selectedWorkstation?: string;
  onSelectWorkstation(w: string): void;
}) => {
  // const cad_image: string | undefined = API.something();
  const [cad_image, set_cad_image] = useState<string>('/line.jpeg');
  const { data: line, isLoading, error } = useMachineStatus();

  if (!cad_image) {
    return <ImageUploadZone onUpload={() => set_cad_image('/line.jpeg')} />;
  }

  if (!line || isLoading) return <ResLoading />;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="relative w-3/4 m-6 p-2 border border-neutral-300 rounded-xl">
      <DndProvider backend={HTML5Backend}>
        <div
          className="relative h-full w-full bg-no-repeat bg-center bg-contain bg-neutral-100"
          style={{
            backgroundImage: `url(${cad_image})`,
          }}>
          <DraggableDots
            selected={ps.selectedWorkstation}
            dots={line.workcenter_info.subcenters.map((sc) => {
              const position = JSON.parse(sc.sub_ws_position || '[]') as [
                number,
                number,
              ];
              return {
                id: String(sc.sub_ws_id),
                x: position[0],
                y: position[1],
                onSelect: () => ps.onSelectWorkstation(String(sc.sub_ws_id)),
              };
            })}
          />
          <div
            className="absolute top-0 left-0 m-6 p-2 bg-transparent rounded-lg border border-green-500 cursor-pointer hover:scale-105 transition-transform duration-200 text-green-500 flex items-center gap-2"
            onClick={() => {
              alert('Adding dot');
            }}>
            <AddPinIcon className="w-8 h-8" />
            Aggiungi stazione
          </div>
          <div
            className="absolute top-0 right-0 m-6 p-2 bg-transparent rounded-lg border border-red-500 cursor-pointer hover:scale-105 transition-transform duration-200"
            onClick={() => {
              const answer = window.confirm(
                "Sei sicuro di voler eliminare l'immagine?",
              );
              if (answer) {
                set_cad_image('');
              }
            }}>
            <TrashIcon className="w-8 h-8" />
          </div>
        </div>
      </DndProvider>
    </div>
  );
};

const ImageUploadZone = (ps: { onUpload(file: File): void }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <form className="relative w-3/4 m-6 p-2 border border-neutral-300 rounded-xl">
      <button
        className="cursor-pointer h-full w-full bg-neutral-100 rounded-md flex flex-col items-center justify-center gap-4 text-neutral-500 text-center"
        onClick={(e) => {
          e.preventDefault();
          if (!inputRef || !inputRef.current) return;

          inputRef.current.click();
        }}>
        <UploadIcon className="w-16 h-16" />
        <h2 className="font-bold text-3xl">Carica Immagine</h2>
        <p className="font-light">
          Nei formati .jpg o .png
          <br />
          Dimensione minima: 1024*768px
        </p>
      </button>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept="image/png, image/jpeg, image/jpg"
        onChange={(e) => {
          const files = e.target.files;
          if (!files) return;

          const file = files[0];
          ps.onUpload(file);
        }}
      />
    </form>
  );
};

interface DotProps {
  id: string;
  x: number;
  y: number;
  onSelect(): void;
  active?: boolean;
}

const Dot: React.FC<DotProps> = ({ id, x, y, onSelect, active }) => {
  const [, drag] = useDrag(() => ({
    type: 'dot',
    item: { id, x, y },
  }));

  return (
    <div
      ref={drag}
      onClick={onSelect}
      className={clsx(
        'group w-[34px] h-[34px] rounded-full absolute cursor-pointer transition-transform text-center border-4 border-white bg-green-500',
        active ? 'scale-110' : 'hover:scale-110',
      )}
      style={{
        left: `${x}px`,
        top: `${y}px`,
      }}>
      {/* <span
        className={clsx(
          active ? 'inline' : 'hidden group-hover:inline',
          'absolute -top-[66px] -left-[68px] w-[160px] h-[160px] bg-opacity-10 border-4 rounded-full bg-green-500 border-green-500',
        )}
      /> */}
    </div>
  );

  // return (
  //   <div
  //     ref={drag}
  //     className={clsx(
  //       'w-6 h-6 rounded-full cursor-pointer absolute bg-green-500 hover:scale-110 transition-transform duration-200',
  //     )}
  //     style={{
  //       left: `${x}px`,
  //       top: `${y}px`,
  //     }}
  //     onClick={onSelect}>
  //     <span
  //       className={clsx(
  //         active ? 'inline' : 'hidden group-hover:inline',
  //         'absolute -top-[66px] -left-[68px] w-[160px] h-[160px] bg-opacity-10 border-4 rounded-full border-green-500 bg-green-500',
  //       )}
  //     />
  //   </div>
  // );
};

const DraggableDots = (ps: { dots: DotProps[]; selected?: string }) => {
  const [dots, setDots] = useState(ps.dots);

  const [, drop] = useDrop(() => ({
    accept: 'dot',
    drop: (item: { id: string; x: number; y: number }, monitor) => {
      const delta = monitor.getDifferenceFromInitialOffset() || { x: 0, y: 0 };
      setDots((prev) =>
        prev.map((dot) =>
          dot.id === item.id
            ? { ...dot, x: dot.x + delta.x, y: dot.y + delta.y }
            : dot,
        ),
      );
    },
  }));

  return (
    <div ref={drop} className="relative w-full h-full bg-transparent">
      {dots.map((dot) => (
        <Dot key={dot.id} {...dot} active={dot.id === ps.selected} />
      ))}
    </div>
  );
};

export default DraggableDots;
